import { FC, useMemo, useRef, useState } from "react";
import { Paper } from "@mui/material";
import {
  GridFilterModel,
  GridLogicOperator,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { isNumber } from "lodash";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  absencesAbsenceRequestsEdit,
  absencesApprovableAbsenceRequestsEdit,
} from "src/utils/RoleBasedSecurity/permissions";
import useIsAhr from "src/utils/hooks/useIsAhr";
import useBasePath from "src/utils/hooks/useBasePath";
import { useFetchManagedAbsenceRequests } from "src/Pages/Company/Absences/api/useAbsenceRequest";
import parseTableFilters from "src/utils/Company/Absences/parseTableFilters";
import Title from "src/Pages/Company/Absences/Title/Title";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import AbsenceRequestsTable from "src/Pages/Company/Absences/AbsenceRequests/AbsenceRequestsTable";
import PermissionsAlert from "src/Pages/Company/Absences/PermissionsAlert";

const AbsenceRequests: FC = () => {
  const isAhr = useIsAhr();
  const basePath = useBasePath();
  const canAHREditAbsenceRequests = usePermission(
    absencesApprovableAbsenceRequestsEdit
  );
  const canOMBEditAbsenceRequests = usePermission(absencesAbsenceRequestsEdit);
  const canEditAbsenceRequests = isAhr
    ? canAHREditAbsenceRequests
    : canOMBEditAbsenceRequests;

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });

  const requestParams = useMemo(
    () => ({
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
      sort: sortModel?.map(({ field, sort }) => `${field},${sort}`),
      filters: parseTableFilters(filterModel),
    }),
    [filterModel, paginationModel.page, paginationModel.pageSize, sortModel]
  );

  const { data, isFetching } = useFetchManagedAbsenceRequests(requestParams);

  const rowCountRef = useRef(data?.totalElements || 0);
  const rowCount = useMemo(() => {
    if (isNumber(data?.totalElements)) {
      rowCountRef.current = data.totalElements;
    }
    return rowCountRef.current;
  }, [data?.totalElements]);

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Lista złożonych wniosków"),
          },
        ]}
      />
      <Title
        title={__("Lista złożonych wniosków")}
        subtitle={__(
          "Przestrzeń do zarządzania nieobecnościami i statusami obecności u pracowników."
        )}
      />
      {!canEditAbsenceRequests && <PermissionsAlert />}
      <Paper>
        <AbsenceRequestsTable
          loading={isFetching}
          rows={data?.content || []}
          rowCount={rowCount}
          storageKey="absence-request-table-state"
          sortModel={sortModel}
          setSortModel={setSortModel}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          filterModel={filterModel}
          setFilterModel={setFilterModel}
        />
      </Paper>
    </>
  );
};

export default AbsenceRequests;
